import React, { useEffect, useState } from 'react';
import { FiX, FiUpload } from 'react-icons/fi';

const toInputDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const AdFormModal = ({ open, editingAd, onClose, onSubmit, submitting }) => {
  const [formData, setFormData] = useState({
    advertiserName: '',
    title: '',
    description: '',
    landingUrl: '',
    clickUrl: '',
    startDate: '',
    endDate: '',
    weight: 1,
    status: 'draft',
    displayMode: 'popup',
    popupDelay: 0,
    closeable: true,
    closeOnBackdrop: true,
    showOncePerSession: true,
    popupMaxWidth: 430
  });
  const [creativeFile, setCreativeFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (!open) return;

    if (editingAd) {
      setFormData({
        advertiserName: editingAd.advertiserName || '',
        title: editingAd.title || '',
        description: editingAd.description || '',
        landingUrl: editingAd.landingUrl || '',
        clickUrl: editingAd.clickUrl || '',
        startDate: toInputDate(editingAd.startDate),
        endDate: toInputDate(editingAd.endDate),
        weight: editingAd.weight || 1,
        status: editingAd.status || 'draft',
        displayMode: editingAd.displaySettings?.displayMode || 'popup',
        popupDelay: editingAd.displaySettings?.popupDelay ?? 0,
        closeable: editingAd.displaySettings?.closeable !== false,
        closeOnBackdrop: editingAd.displaySettings?.closeOnBackdrop !== false,
        showOncePerSession: editingAd.displaySettings?.showOncePerSession !== false,
        popupMaxWidth: editingAd.displaySettings?.popupMaxWidth ?? 430
      });
      setPreviewUrl(editingAd.creative?.url || '');
    } else {
      setFormData({
        advertiserName: '',
        title: '',
        description: '',
        landingUrl: '',
        clickUrl: '',
        startDate: '',
        endDate: '',
        weight: 1,
        status: 'draft',
        displayMode: 'popup',
        popupDelay: 0,
        closeable: true,
        closeOnBackdrop: true,
        showOncePerSession: true,
        popupMaxWidth: 430
      });
      setPreviewUrl('');
    }
    setCreativeFile(null);
  }, [open, editingAd]);

  useEffect(() => {
    if (!creativeFile) return undefined;
    const objectUrl = URL.createObjectURL(creativeFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [creativeFile]);

  if (!open) return null;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({ ...formData, creativeFile });
  };

  const isVideoPreview = creativeFile
    ? creativeFile.type.startsWith('video/')
    : editingAd?.creativeType === 'video';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-xl">
        <div className="sticky top-0 flex items-center justify-between border-b bg-white px-6 py-4">
          <h2 className="text-xl font-semibold text-slate-900">
            {editingAd ? 'Edit Advertisement' : 'Create Advertisement'}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-700">
            <FiX size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Advertiser Name *" name="advertiserName" value={formData.advertiserName} onChange={handleChange} required />
            <Field label="Ad Title" name="title" value={formData.title} onChange={handleChange} />
            <Field label="Landing URL *" name="landingUrl" value={formData.landingUrl} onChange={handleChange} required className="md:col-span-2" placeholder="https://advertiser.com/landing" />
            <Field label="Click URL (optional)" name="clickUrl" value={formData.clickUrl} onChange={handleChange} className="md:col-span-2" placeholder="Optional tracking URL before landing page" />
            <Field label="Start Date *" name="startDate" type="date" value={formData.startDate} onChange={handleChange} required />
            <Field label="End Date *" name="endDate" type="date" value={formData.endDate} onChange={handleChange} required />
            <Field label="Priority Weight (1-100)" name="weight" type="number" min="1" max="100" value={formData.weight} onChange={handleChange} />
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Status</label>
              <select name="status" value={formData.status} onChange={handleChange} className="w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-orange-500">
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900">Display settings</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Display mode</label>
                <select name="displayMode" value={formData.displayMode} onChange={handleChange} className="w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-orange-500">
                  <option value="popup">Popup overlay (recommended)</option>
                  <option value="inline">Inline slot</option>
                </select>
              </div>
              <Field label="Popup delay (ms)" name="popupDelay" type="number" min="0" max="30000" value={formData.popupDelay} onChange={handleChange} />
              <Field label="Popup max width (px)" name="popupMaxWidth" type="number" min="280" max="800" value={formData.popupMaxWidth} onChange={handleChange} />
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-slate-700">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" name="closeable" checked={formData.closeable} onChange={handleChange} />
                Show close button
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" name="closeOnBackdrop" checked={formData.closeOnBackdrop} onChange={handleChange} />
                Close on backdrop click
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" name="showOncePerSession" checked={formData.showOncePerSession} onChange={handleChange} />
                Show once per session
              </label>
            </div>
            <p className="text-xs text-slate-500">
              Popup mode: publishers paste one script tag only. The SDK renders a centered overlay using Shadow DOM.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-orange-500"
              placeholder="Internal notes about this advertisement"
            />
          </div>

          <div className="rounded-xl border border-dashed border-slate-300 p-4">
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Creative {editingAd ? '(replace existing)' : '*'}
            </label>
            <p className="mb-3 text-xs text-slate-500">Upload one image, GIF, or video file at a time.</p>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <FiUpload />
              Choose file
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
                className="hidden"
                onChange={(event) => setCreativeFile(event.target.files?.[0] || null)}
              />
            </label>
            {creativeFile && <p className="mt-2 text-sm text-slate-600">Selected: {creativeFile.name}</p>}
            {previewUrl && (
              <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 p-3">
                {isVideoPreview ? (
                  <video src={previewUrl} controls className="max-h-56 w-full rounded-lg object-contain" />
                ) : (
                  <img src={previewUrl} alt="Creative preview" className="max-h-56 w-full rounded-lg object-contain" />
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t pt-4">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-60"
            >
              {submitting ? 'Saving…' : editingAd ? 'Update Advertisement' : 'Create Advertisement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, className = '', ...props }) => (
  <div className={className}>
    <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
    <input
      {...props}
      className="w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-orange-500"
    />
  </div>
);

export default AdFormModal;
